import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import {
  ArrowLeft, Settings, Loader2, Copy, Check, Wifi, WifiOff,
  ExternalLink, Save, ShoppingBag, Clock, Truck, AlertCircle, RefreshCw
} from "lucide-react";
import { API_URL } from "@/utils/apiConfig";

const API = API_URL;

const CHANNEL_LOGOS = {
  ubereats: { name: "Uber Eats", color: "bg-green-600", text: "#06C167" },
  glovo: { name: "Glovo", color: "bg-yellow-500", text: "#FFC244" },
  bolt_food: { name: "Bolt Food", color: "bg-emerald-500", text: "#34D186" },
  deliveroo: { name: "Deliveroo", color: "bg-teal-500", text: "#00CCBC" },
  just_eat: { name: "Just Eat", color: "bg-orange-500", text: "#FF8000" },
  wolt: { name: "Wolt", color: "bg-blue-500", text: "#009DE0" },
};

export default function IntegrationsPage() {
  const { authAxios, user } = useAuth();
  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;
  const [settings, setSettings] = useState(null);
  const [orders, setOrders] = useState([]);
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [tab, setTab] = useState("config");

  const webhookUrl = `${API}/api/v1/aggregators/webhook/${user?.restaurant_id}`;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    Promise.all([
      authAxiosRef.current.get("/aggregators/settings").then(r => setSettings(r.data)),
      authAxiosRef.current.get("/aggregators/channels").then(r => setChannels(r.data.channels || [])),
      authAxiosRef.current.get("/aggregators/orders").then(r => setOrders(r.data || [])).catch(() => {}),
    ]).catch(() => toast.error("Erro ao carregar configuracoes"))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await authAxios.put("/aggregators/settings", settings);
      toast.success("Configuracoes salvas!");
    } catch { toast.error("Erro ao salvar"); }
    setSaving(false);
  };

  const copyWebhook = () => {
    navigator.clipboard.writeText(webhookUrl);
    setCopied(true);
    toast.success("URL copiada!");
    setTimeout(() => setCopied(false), 2000);
  };

  const refreshOrders = async () => {
    try {
      const r = await authAxios.get("/aggregators/orders");
      setOrders(r.data || []);
      toast.success("Pedidos atualizados");
    } catch { toast.error("Erro"); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  const statusLabels = { pending: "Pendente", preparing: "Preparando", ready: "Pronto", delivered: "Entregue", cancelled: "Cancelado" };
  const statusColors = { pending: "bg-yellow-100 text-yellow-700", preparing: "bg-blue-100 text-blue-700", ready: "bg-emerald-100 text-emerald-700", delivered: "bg-gray-100 text-gray-500", cancelled: "bg-red-100 text-red-600" };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="integrations-page">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-3">
          <a href="/settings" className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center hover:bg-gray-200">
            <ArrowLeft className="w-4 h-4" />
          </a>
          <Settings className="w-5 h-5 text-emerald-600" />
          <h1 className="text-lg font-bold flex-1">Integracoes - Agregadores</h1>
          <Badge className={settings?.enabled ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"}>
            {settings?.enabled ? <><Wifi className="w-3 h-3 mr-1" /> Ativo</> : <><WifiOff className="w-3 h-3 mr-1" /> Inativo</>}
          </Badge>
        </div>
        {/* Tabs */}
        <div className="max-w-4xl mx-auto px-4 flex gap-0">
          {[
            { id: "config", label: "Configuracao" },
            { id: "orders", label: `Pedidos (${orders.length})` },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${tab === t.id ? "text-emerald-600 border-emerald-600" : "text-gray-400 border-transparent hover:text-gray-600"}`}
              data-testid={`tab-${t.id}`}
            >{t.label}</button>
          ))}
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4 space-y-4">
        {tab === "config" ? (
          <>
            {/* How it works */}
            <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl p-4" data-testid="how-it-works">
              <h3 className="font-bold text-sm text-emerald-800 mb-2">Como funciona?</h3>
              <div className="text-xs text-emerald-700 space-y-1.5">
                <p>1. Crie uma conta no <strong>Deliverect</strong> (deliverect.com) e conecte seus agregadores (Uber Eats, Glovo, Bolt Food, etc.)</p>
                <p>2. No painel do Deliverect, va em <strong>Locations &gt; Edit &gt; Generic POS Settings</strong></p>
                <p>3. Cole a <strong>URL do Webhook</strong> abaixo no campo "Orders webhook URL"</p>
                <p>4. Pronto! Os pedidos dos agregadores cairao automaticamente no Kaiso System.</p>
              </div>
              <a href="https://www.deliverect.com" target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600 hover:underline mt-2"
              >Ir para Deliverect <ExternalLink className="w-3 h-3" /></a>
            </div>

            {/* Enable Toggle */}
            <div className="bg-white rounded-xl border p-4" data-testid="enable-toggle">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-bold text-gray-800">Ativar integracoes</Label>
                  <p className="text-xs text-gray-400 mt-0.5">Receba pedidos de agregadores automaticamente</p>
                </div>
                <Switch
                  checked={settings?.enabled || false}
                  onCheckedChange={v => setSettings(prev => ({ ...prev, enabled: v }))}
                  data-testid="integration-enabled-toggle"
                />
              </div>
            </div>

            {/* Webhook URL */}
            <div className="bg-white rounded-xl border p-4" data-testid="webhook-section">
              <Label className="text-xs text-gray-500 mb-2 block">URL do Webhook (cole no Deliverect)</Label>
              <div className="flex gap-2">
                <Input
                  value={webhookUrl}
                  readOnly
                  className="font-mono text-xs bg-gray-50"
                  data-testid="webhook-url-input"
                />
                <Button size="sm" variant="outline" onClick={copyWebhook} className="flex-shrink-0 gap-1.5" data-testid="copy-webhook-btn">
                  {copied ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                  {copied ? "Copiado" : "Copiar"}
                </Button>
              </div>
              <p className="text-[10px] text-gray-400 mt-1.5">Status updates URL: {API}/api/v1/aggregators/webhook/{user?.restaurant_id}/status</p>
            </div>

            {/* HMAC Secret */}
            <div className="bg-white rounded-xl border p-4" data-testid="hmac-section">
              <Label className="text-xs text-gray-500 mb-2 block">Webhook Secret (HMAC - opcional)</Label>
              <Input
                type="password"
                value={settings?.webhook_secret || ""}
                onChange={e => setSettings(prev => ({ ...prev, webhook_secret: e.target.value }))}
                placeholder="Cole o secret do Deliverect para verificacao HMAC"
                className="font-mono text-xs"
                data-testid="hmac-secret-input"
              />
              <p className="text-[10px] text-gray-400 mt-1">Seguranca extra: Deliverect envia um hash HMAC em cada webhook para validar a autenticidade</p>
            </div>

            {/* Auto-accept & Auto-print */}
            <div className="bg-white rounded-xl border p-4 space-y-3" data-testid="auto-settings">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm text-gray-800">Aceitar pedidos automaticamente</Label>
                  <p className="text-[10px] text-gray-400">Pedidos sao aceitos sem confirmacao manual</p>
                </div>
                <Switch
                  checked={settings?.auto_accept !== false}
                  onCheckedChange={v => setSettings(prev => ({ ...prev, auto_accept: v }))}
                  data-testid="auto-accept-toggle"
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm text-gray-800">Imprimir automaticamente</Label>
                  <p className="text-[10px] text-gray-400">Enviar pedido direto para a impressora</p>
                </div>
                <Switch
                  checked={settings?.auto_print || false}
                  onCheckedChange={v => setSettings(prev => ({ ...prev, auto_print: v }))}
                  data-testid="auto-print-toggle"
                />
              </div>
            </div>

            {/* Supported Channels */}
            <div className="bg-white rounded-xl border p-4" data-testid="channels-section">
              <h3 className="font-bold text-sm text-gray-800 mb-3">Agregadores suportados via Deliverect</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {channels.map(ch => {
                  const info = CHANNEL_LOGOS[ch.id] || {};
                  return (
                    <div key={ch.id} className="flex items-center gap-2.5 p-2.5 rounded-lg border bg-gray-50" data-testid={`channel-${ch.id}`}>
                      <div className={`w-8 h-8 rounded-lg ${info.color || "bg-gray-400"} flex items-center justify-center`}>
                        <span className="text-white text-[10px] font-black">{ch.name.slice(0, 2).toUpperCase()}</span>
                      </div>
                      <span className="text-xs font-medium text-gray-700">{ch.name}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Save Button */}
            <Button
              className="w-full bg-emerald-600 hover:bg-emerald-700 font-bold gap-2 h-11"
              onClick={handleSave}
              disabled={saving}
              data-testid="save-settings-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Salvar Configuracoes
            </Button>
          </>
        ) : (
          /* Orders Tab */
          <>
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-sm text-gray-700">Pedidos de agregadores hoje</h3>
              <Button size="sm" variant="outline" onClick={refreshOrders} className="gap-1.5 text-xs" data-testid="refresh-orders-btn">
                <RefreshCw className="w-3 h-3" /> Atualizar
              </Button>
            </div>

            {orders.length === 0 ? (
              <div className="bg-white rounded-xl border p-8 text-center">
                <ShoppingBag className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <p className="text-sm text-gray-400 mb-1">Nenhum pedido de agregadores hoje</p>
                <p className="text-xs text-gray-300">Os pedidos aparecerao aqui quando chegarem via Deliverect</p>
              </div>
            ) : (
              <div className="space-y-2">
                {orders.map(order => {
                  const chInfo = CHANNEL_LOGOS[order.source] || {};
                  return (
                    <div key={order.id} className="bg-white rounded-xl border p-3 flex items-center gap-3" data-testid={`agg-order-${order.id?.slice(-6)}`}>
                      <div className={`w-10 h-10 rounded-lg ${chInfo.color || "bg-gray-400"} flex items-center justify-center flex-shrink-0`}>
                        <span className="text-white text-[10px] font-black">{(order.channel_label || order.source || "?").slice(0, 2).toUpperCase()}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="font-bold text-xs">#{order.channel_display_id || order.id?.slice(-6)}</span>
                          <Badge className={`text-[9px] ${statusColors[order.status] || "bg-gray-100"}`}>
                            {statusLabels[order.status] || order.status}
                          </Badge>
                          <Badge className="text-[9px] bg-purple-50 text-purple-600 border-0">
                            {order.channel_label || order.source}
                          </Badge>
                        </div>
                        <p className="text-[11px] text-gray-500 truncate">
                          {order.customer_name || "Cliente"} - {(order.items || []).length} itens
                          {order.order_type === "delivery" && <span className="inline-flex items-center gap-0.5 ml-1"><Truck className="w-3 h-3" /></span>}
                        </p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-sm font-bold text-emerald-600">{(order.total || 0).toFixed(2)} EUR</p>
                        <p className="text-[9px] text-gray-400 flex items-center gap-0.5 justify-end">
                          <Clock className="w-2.5 h-2.5" />
                          {order.created_at ? new Date(order.created_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }) : ""}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {!settings?.enabled && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3" data-testid="not-enabled-warning">
                <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-800">Integracoes desativadas</p>
                  <p className="text-xs text-amber-600 mt-0.5">Ative as integracoes na aba Configuracao para comecar a receber pedidos</p>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
